import React, { useState, useEffect } from "react";
import { Api } from "../../network/api";

const ResultDetailModal = ({ result, onClose, aiResponseData, loading = false }) => {
  const [resultData, setResultData] = useState(null);
  const [loadingData, setLoadingData] = useState(true);

  const getScoreColor = (percentage) => {
    if (percentage >= 90)
      return { color: "#059669", bg: "#d1fae5", text: "Excellent" };
    if (percentage >= 80)
      return { color: "#10b981", bg: "#d1fae5", text: "Good" };
    if (percentage >= 70)
      return { color: "#f59e0b", bg: "#fef3c7", text: "Satisfactory" };
    if (percentage >= 60)
      return { color: "#f97316", bg: "#fed7aa", text: "Needs Improvement" };
    if (percentage >= 50)
      return { color: "#ef4444", bg: "#fecaca", text: "Poor" };
    return { color: "#dc2626", bg: "#fecaca", text: "Failing" };
  };

  const createScorecardFromIRAC = (data) => {
    if (!data) return {};
    console.log("Data:", data);

    if (data.scorecard) {
      try {
        if (typeof data.scorecard === "object") {
          return data.scorecard;
        }
        if (typeof data.scorecard === "string") {
          let raw = data?.scorecard.trim();
          if (raw.startsWith("{'") || raw.includes("':")) {
            raw = raw
              .replace(/'/g, '"')
              .replace(/\bNone\b/g, "null");
          }

          return JSON.parse(raw);
        }
      } catch (error) {
        console.warn("Error parsing scorecard from backend:", error);
      }
    }

    return {
      "Issue Spotting": {
        total_marks: data.irac_issue_spotting_total,
        obtained_marks: data.irac_issue_spotting_obtained,
      },
      "Rule Statements": {
        total_marks: data.irac_rule_statements_total,
        obtained_marks: data.irac_rule_statements_obtained,
      },
      "Analysis Depth": {
        total_marks: data.irac_analysis_depth_total,
        obtained_marks: data.irac_analysis_depth_obtained,
      },
      "Conclusion": {
        total_marks: data.irac_conclusion_accuracy_total,
        obtained_marks: data.irac_conclusion_accuracy_obtained,
      },
      Organization: {
        total_marks: data.irac_organization_total,
        obtained_marks: data.irac_organization_obtained,
      },
      "Writing Style": {
        total_marks: data.irac_writing_style_total,
        obtained_marks: data.irac_writing_style_obtained,
      },
    };
  };

  useEffect(() => {
    const fetchResultDetails = async () => {
      setLoadingData(true);
      try {
        if (aiResponseData) {
          setResultData(aiResponseData);
        } else if (result?.id) {
          const response = await Api.getResultDetails(result.id);
          console.log("API Response:", response.data);
          setResultData(response.data);
        }
        setLoadingData(false);
      } catch (error) {
        console.error("Error loading result details:", error);
        setLoadingData(false);
      }
    };

    fetchResultDetails();
  }, [result?.id, aiResponseData]);

  const displayData = resultData;
  const scorecardData = createScorecardFromIRAC(displayData);
  const scorecardCategories = scorecardData && typeof scorecardData === 'object'
    ? Object.keys(scorecardData)
    : [];

  if (loading || loadingData || !displayData) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg max-w-6xl w-full max-h-[95vh] overflow-y-auto">
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading analysis details...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-7xl w-full max-h-[95vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {result?.subject_name || "Essay Analysis"}
                </h1>
                <p className="text-sm text-gray-600 mt-1">
                  {/* Student: {result?.user_name || "Unknown User"} */}
                   Submitted:
                  {result?.submit_date ? new Date(result.submit_date).toLocaleDateString() : "N/A"}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700">
                  Status:
                </label>
                <span
                  className={`px-2 py-1 rounded-full text-xs font-medium ${
                    displayData.status === "completed"
                      ? "bg-green-100 text-green-800"
                      : displayData.status === "pending"
                      ? "bg-yellow-100 text-yellow-800"
                      : displayData.status === "flagged"
                      ? "bg-red-100 text-red-800"
                      : "bg-gray-100 text-gray-800"
                  }`}
                >
                  {displayData.status || result?.status || "Unknown"}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="p-6 space-y-6 text-left">
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Submission Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm md:text-lg font-bold text-gray-700 mb-1">
                  Subject:
                </label>
                <p className="text-sm text-gray-900 text-left">
                  {displayData.subject || "N/A"}
                </p>
              </div>
              <div>
                <label className="block text-sm md:text-lg font-bold text-gray-700 mb-1">
                  Student Filename:
                </label>
                <p className="text-sm text-gray-900 text-left">
                  {displayData.original_filename || "N/A"}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Overall Performance
                </h3>
              </div>
              <div className="text-center">
                <div
                  className="text-4xl font-bold"
                  style={{
                    color: getScoreColor(displayData.percentage || displayData.percentage_score).color,
                  }}
                >
                  {displayData.score || displayData.total_score}
                </div>
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Detailed Scorecard
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {scorecardCategories.map((category) => {
                const scorecard = createScorecardFromIRAC(displayData);
                const scoreObj = scorecard[category];
                const score = scoreObj?.obtained_marks || 0;
                const maxScore = scoreObj?.total_marks || 0;
                const percentage = maxScore > 0 ? (score / maxScore) * 100 : 0;
                const colorInfo = getScoreColor(percentage);

                return (
                  <div
                    key={category}
                    className="bg-white border border-gray-200 rounded-lg p-4"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-gray-900">{category}</h4>
                      <div className="text-right">
                        <span
                          className="text-lg font-bold"
                          style={{ color: colorInfo.color }}
                        >
                          {score} / {maxScore}
                        </span>
                        <div
                          className="text-xs font-medium"
                          style={{ color: colorInfo.color }}
                        >
                          {percentage.toFixed(1)}%
                        </div>
                      </div>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
                      <div
                        className="h-3 rounded-full transition-all duration-300"
                        style={{
                          width: `${percentage}%`,
                          backgroundColor: colorInfo.color,
                        }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Grade Summary
            </h3>
            <p className="text-gray-700 leading-relaxed text-left">
              {displayData.grade_summary}
            </p>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Detailed Feedback & Analysis
            </h3>
            <div className="space-y-4">
              {scorecardCategories.map((category, index) => (
                <div
                  key={category}
                  className="grid grid-cols-1 lg:grid-cols-3 gap-6"
                >
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-gray-900">
                        {category}
                      </h4>
                    </div>

                    <div>
                      <h5 className="text-sm font-medium text-gray-700 mb-2">
                        Feedback:
                      </h5>
                      <p className="text-sm text-gray-600 leading-relaxed text-left">
                        {displayData.feedback?.[category] ||
                          "No feedback available"}
                      </p>
                    </div>
                  </div>
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-gray-900">
                        {category}
                      </h4>
                      <span className="text-xs text-gray-500">Analysis</span>
                    </div>
                    <div>
                      <h5 className="text-sm font-medium text-gray-700 mb-2">
                        Analysis:
                      </h5>
                      <p className="text-sm text-gray-600 leading-relaxed text-left">
                        {displayData?.analysis?.[category] ||
                          "No analysis available"}
                      </p>
                    </div>
                  </div>
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-gray-900">
                        Improvement Example
                      </h4>
                      <span className="text-xs text-gray-500">Improvement</span>
                    </div>
                    <div className="bg-green-50 border border-green-200 rounded p-3">
                      <p className="text-sm text-gray-700 italic leading-relaxed text-left">
                        "
                        {displayData?.re_write_example?.[category] ||
                          "No improvement example available"}
                        "
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {(displayData.irac_issue_spotting_total || displayData.irac_rule_statements_total || displayData.irac_analysis_depth_total || displayData.irac_conclusion_accuracy_total || displayData.irac_organization_total || displayData.irac_writing_style_total) && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                IRAC Breakdown
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  {
                    name: "Issue Spotting",
                    obtained: displayData.irac_issue_spotting_obtained,
                    total: displayData.irac_issue_spotting_total,
                  },
                  {
                    name: "Rule Statements",
                    obtained: displayData.irac_rule_statements_obtained,
                    total: displayData.irac_rule_statements_total,
                  },
                  {
                    name: "Analysis Depth",
                    obtained: displayData.irac_analysis_depth_obtained,
                    total: displayData.irac_analysis_depth_total,
                  },
                  {
                    name: "Conclusion",
                    obtained: displayData.irac_conclusion_accuracy_obtained,
                    total: displayData.irac_conclusion_accuracy_total,
                  },
                  {
                    name: "Organization",
                    obtained: displayData.irac_organization_obtained,
                    total: displayData.irac_organization_total,
                  },
                  {
                    name: "Writing Style",
                    obtained: displayData.irac_writing_style_obtained,
                    total: displayData.irac_writing_style_total,
                  },
                ]
                  .filter(item => item.total > 0)
                  .map((item) => {
                    const percentage = item.total > 0 ? (item.obtained / item.total) * 100 : 0;
                    const colorInfo = getScoreColor(percentage);

                    return (
                      <div
                        key={item.name}
                        className="bg-white border border-gray-200 rounded-lg p-4"
                      >
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-semibold text-gray-900">
                            {item.name}
                          </h4>
                          <div className="text-right">
                            <div
                              className="text-lg font-bold"
                              style={{ color: colorInfo.color }}
                            >
                              {item.obtained} / {item.total}
                            </div>
                            <div
                              className="text-xs font-medium"
                              style={{ color: colorInfo.color }}
                            >
                              {percentage.toFixed(1)}%
                            </div>
                          </div>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3">
                          <div
                            className="h-3 rounded-full transition-all duration-300"
                            style={{
                              width: `${percentage}%`,
                              backgroundColor: colorInfo.color,
                            }}
                          ></div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}

          {displayData.missing_topics_analysis && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Missing Topics Analysis
              </h3>
              <div className="flex flex-col lg:flex-row gap-6 justify-around">
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900 mb-2">
                  Missed Topics:
                  </h4>
                  <ul className="space-y-1">
                    {displayData.missing_topics_analysis.topics_from_model_student_missed?.map(
                      (topic, index) => (
                        <li
                          key={index}
                          className="flex items-start"
                        >
                          <span className="text-orange-500 mr-2 -mt-1">
                            •
                          </span>
                          <span className="text-gray-700">
                            {topic}
                          </span>
                        </li>
                      )
                    )}
                  </ul>
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900 mb-2">
                    Specific Missing Content:
                  </h4>
                  <ul className="space-y-1">
                    {displayData.missing_topics_analysis.specific_missing_content?.map(
                      (content, index) => (
                        <li
                          key={index}
                          className="flex items-start"
                        >
                          <span className="text-orange-500 mr-2 -mt-1">
                            •
                          </span>
                          <span className="text-gray-700">
                            {content}
                          </span>
                        </li>
                      )
                    )}
                  </ul>
                </div>
              </div>
            </div>
          )}

          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              Missed Issues
            </h3>
            <ul className="space-y-2">
              {displayData.missed_issues?.map((issue, index) => (
                <li key={index} className="flex items-start">
                  <span className="text-red-500 mr-2 -mt-1">•</span>
                  <span className="text-gray-700">{issue}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
            >
              Close
            </button>
            {/* <button
              className="px-4 py-2 text-white rounded-md transition-colors"
              style={{ backgroundColor: theme.Colors.primary }}
            >
              Download Report
            </button> */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResultDetailModal;